import React, { memo, useContext, useMemo } from 'react';
import {
  StyledContent,
  StyledThumbNail,
  Text,
  StyledNameCol,
  StyledHyperLink,
} from './OwnTeamsDialog.styles';
import { ArrowDropDown, ArrowDropUp } from '@mui/icons-material';
import moment from 'moment';
import Table from 'component/Table';
import { GridColDef, GridOverlay } from '@mui/x-data-grid';
import HlxButton from 'component/commonComponents/button/button';
import { ThemeContext } from 'styled-components';
import GeneralDialog from '../generalDialog/generalDialog';

const formatDate = (date: string, format?: string) => {
  return format ? moment(date).format(format) : moment(date).fromNow();
};

const CellWrapper = ({
  children,
}: {
  children: React.ReactNode;
}): JSX.Element => {
  return <Text className='spacing-left'>{children}</Text>;
};

const ThumbNail = ({
  children,
}: {
  children: React.ReactNode;
}): JSX.Element => {
  return (
    <StyledThumbNail>
      <div>{children}</div>
    </StyledThumbNail>
  );
};

const convertFirstCharacterEachWord = (str: string): string => {
  const matches = str.match(/\b(\w)/g);
  const result = matches?.join('') || '';
  return (result?.length > 2 ? result?.substring(0, 2) : result);
};

interface IOwnTeamsItem {
  createdOn: string;
  description: string;
  displayName: string;
  id: string;
  mail: string;
  mailEnabled: boolean;
  mailNickname: string;
  renewedDateTime: string;
  resourceBehaviorOptions: string[];
  securityEnabled: boolean;
  visibility: string;
  webUrl: string;
}

export interface IOwnTeamsDialog {
  isOpen: boolean;
  ownTeamsList: IOwnTeamsItem[];
  onClose: () => void;
  locale?: any;
}

const OwnTeamsDialog: React.FC<IOwnTeamsDialog> = ({
  isOpen = false,
  ownTeamsList = [],
  onClose,
  locale,
}): JSX.Element => {
  const theme = useContext(ThemeContext);

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'displayName',
        headerName: locale?.name,
        width: 180,
        sortingOrder: ['desc', 'asc'],
        renderCell: (record) => {
          return (
            <StyledNameCol>
              <ThumbNail>
                {convertFirstCharacterEachWord(record.row.displayName)}
              </ThumbNail>
              <StyledHyperLink
                kind='span'
                label={record.row.displayName}
                onClick={() => window.open(record.row.webUrl, '_blank')}
              />
            </StyledNameCol>
          );
        },
      },
      {
        field: 'description',
        headerName: locale?.description,
        width: 200,
        maxWidth: 280,
        hideSortIcons: true,
        sortable: false,
        renderCell: (record) => {
          return <CellWrapper>{record.row.description}</CellWrapper>;
        },
      },
      {
        field: 'createdOn',
        headerName: locale?.createdOn,
        width: 150,
        hideSortIcons: true,
        sortable: false,
        renderCell: (record) => {
          return (
            <CellWrapper>
              {formatDate(record.row.createdOn, 'MMM DD, YYYY')}
            </CellWrapper>
          );
        },
      },
    ],
    [locale]
  );

  const content = useMemo(() => {
    const localeText = {columnHeaderSortIconLabel: locale?.sort};
    return (
      <StyledContent numberOwnTeams={ownTeamsList.length}>
        <Table
          columns={columns}
          dataSource={ownTeamsList}
          hideFooter={true}
          hideFooterSelectedRowCount
          hideFooterPagination
          disableColumnMenu
          disableSelectionOnClick
          showColumnRightBorder={false}
          rowHeight={34}
          headerHeight={30}
          autoHeight={ownTeamsList?.length <= 5}
          initialState={{
            sorting: {
              sortModel: [{ field: 'displayName', sort: 'asc' }],
            },
          }}
          components={{
            ColumnSortedAscendingIcon: ArrowDropUp,
            ColumnSortedDescendingIcon: ArrowDropDown,
            NoRowsOverlay: () => (
              <GridOverlay>
                {locale?.notOwned}
              </GridOverlay>
            )
          }}
          localeText={localeText}
        />
      </StyledContent>
    );
  }, [columns, locale, ownTeamsList]);

  return (
    <GeneralDialog
      isOpen={isOpen}
      headerBackgroundDefault={false}
      handleClose={onClose}
      title={`${locale?.msTeamOwned} (${ownTeamsList.length})`}
      hideButtonCancel={true}
      buttonSubmit={
        <HlxButton
          label={locale?.close}
          onClick={onClose}
          style={{
            minWidth: '100px',
            backgroundColor: theme.colors.primary[0],
          }}
        />
      }
      content={content}
      justifyContentAction='center'
      commonStyles={{ padding: '0.01px' }} //reset padding
    />
  );
};

export default memo(OwnTeamsDialog);

OwnTeamsDialog.defaultProps = {
  locale: {
    name: 'Name',
    description: 'Description',
    createdOn: 'Created on',
    sort: 'sort',
    msTeamOwned: 'MS Teams - Owned',
    close: 'Close',
    notOwned: 'You do not own any Teams.',
  },
};
